import React, { useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, Select } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button, ButtonBar, Checkbox } from '@ygtechnologies/rubus-ui';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMergeState } from '@/utils/hooks';
import { checkSubsetSelection } from '@/utils/arrays';
import { useRooms } from '@/services/rooms';
import {
    ConfiguratorBackendDto,
    useConfiguratorBackends,
    useConfiguratorPreviewRooms,
    useConfiguratorSources,
    useCreateConfiguratorBackend,
    useUpdateConfiguratorBackend,
} from '@/services/configurator-backends';
import theme from './ConfiguratorBackendDialog.module.scss';

interface SelectOption {
    label: string;
    value: string;
}

// Render the dropdown menu in a body-level portal so it isn't clipped by the
// modal's overflow or drawn under the sticky footer.
const menuPortalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuPosition: 'fixed' as const,
    styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
};

interface ConfiguratorBackendDialogProps {
    projectId: number;
    backend: ConfiguratorBackendDto | null;
    show: boolean;
    onClose(): void;
    onSaved?(): void;
}

interface RoomAssignment {
    projectTagId: number;
    roomValue: string | null;
}

interface FormState {
    name: string;
    url: string;
    rooms: RoomAssignment[];
}

function getInitialState(backend: ConfiguratorBackendDto | null): FormState {
    return {
        name: backend?.name ?? '',
        url: backend?.url ?? '',
        rooms: (backend?.rooms ?? []).map((r) => ({ projectTagId: r.projectTagId, roomValue: r.roomValue })),
    };
}

// The Glencoe project key is the last path segment of the configurator URL.
function deriveProjectKey(url: string): string | undefined {
    const parts = url.trim().split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : undefined;
}

export const ConfiguratorBackendDialog: React.FC<ConfiguratorBackendDialogProps> = ({
    projectId,
    backend,
    show,
    onClose,
    onSaved,
}) => {
    const [form, mergeForm, setForm] = useMergeState<FormState>(getInitialState(backend));
    const { data: rooms } = useRooms(projectId);
    const { data: allBackends } = useConfiguratorBackends(projectId);
    const { data: sources, isLoading: sourcesLoading } = useConfiguratorSources(projectId);

    // A room may belong to at most one configurator. Map every room already taken by a *different*
    // configurator to that configurator's name so we can lock it out of this assignment.
    const assignedElsewhere = useMemo(() => {
        const map = new Map<number, string>();
        (allBackends ?? [])
            .filter((b) => b.id !== backend?.id)
            .forEach((b) => b.rooms.forEach((r) => map.set(r.projectTagId, b.name)));
        return map;
    }, [allBackends, backend?.id]);

    const assignableRooms = useMemo(
        () => (rooms ?? []).filter((r) => !assignedElsewhere.has(r.id)),
        [rooms, assignedElsewhere]
    );

    const projectKey = useMemo(() => deriveProjectKey(form.url), [form.url]);
    const { data: previewRooms, isLoading: previewLoading } = useConfiguratorPreviewRooms(projectId, projectKey);

    const sourceOptions = useMemo<SelectOption[]>(
        () => (sources ?? []).map((s) => ({ label: s.id, value: s.url })),
        [sources]
    );

    const glencoeOptions = useMemo<SelectOption[]>(
        () => (previewRooms ?? []).map((r) => ({ label: r.name, value: r.value })),
        [previewRooms]
    );

    const selectedSource = useMemo<SelectOption | null>(() => {
        const match = sourceOptions.find((o) => o.value === form.url);
        if (match) return match;
        // Preserve a stored URL that isn't in the fetched list (e.g. legacy/free-typed).
        return form.url ? { label: form.url, value: form.url } : null;
    }, [sourceOptions, form.url]);

    const handleSourceChange = (option: SelectOption | null) => {
        if (!option) {
            mergeForm({ url: '' });
            return;
        }
        const source = (sources ?? []).find((s) => s.url === option.value);
        mergeForm({ url: option.value, name: source?.name ?? form.name });
    };

    const createMutation = useCreateConfiguratorBackend(projectId);
    const updateMutation = useUpdateConfiguratorBackend(projectId);
    const saving = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (show) {
            setForm(getInitialState(backend));
        }
    }, [show, backend]);

    const isNew = !backend?.id;

    const isSelected = (roomId: number) => form.rooms.some((r) => r.projectTagId === roomId);
    const roomValueOf = (roomId: number) => form.rooms.find((r) => r.projectTagId === roomId)?.roomValue ?? null;

    const toggleRoom = (roomId: number, selected: boolean) => {
        if (selected) {
            mergeForm({ rooms: [...form.rooms, { projectTagId: roomId, roomValue: null }] });
        } else {
            mergeForm({ rooms: form.rooms.filter((r) => r.projectTagId !== roomId) });
        }
    };

    const setRoomValue = (roomId: number, value: string | null) => {
        mergeForm({ rooms: form.rooms.map((r) => (r.projectTagId === roomId ? { ...r, roomValue: value } : r)) });
    };

    const handleSelectAll = (flag: boolean) => {
        if (!flag) {
            mergeForm({ rooms: [] });
            return;
        }
        const existing = new Map(form.rooms.map((r) => [r.projectTagId, r.roomValue]));
        mergeForm({ rooms: assignableRooms.map((r) => ({ projectTagId: r.id, roomValue: existing.get(r.id) ?? null })) });
    };

    const glencoeValue = (roomValue: string | null): SelectOption | null => {
        if (!roomValue) return null;
        return glencoeOptions.find((o) => o.value === roomValue) ?? { label: roomValue, value: roomValue };
    };

    const hasUnmappedRoom = form.rooms.some((r) => !r.roomValue);

    const handleSubmit = async () => {
        const body = { name: form.name.trim(), url: form.url.trim(), rooms: form.rooms };
        if (isNew) {
            await createMutation.mutateAsync(body);
        } else {
            await updateMutation.mutateAsync({ id: backend!.id, body });
        }
        triggerHandler(onSaved);
        triggerHandler(onClose);
    };

    const checkStatus = checkSubsetSelection(
        form.rooms.map((r) => r.projectTagId),
        assignableRooms.map((x) => x.id)
    );

    return (
        <Modal isOpen={show} toggle={onClose}>
            <ValidationForm onSubmit={handleSubmit}>
                <ModalHeader>
                    {isNew ? <Trans>Add 3D configurator</Trans> : <Trans>Edit 3D configurator</Trans>}
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>
                            <Trans>Configurator project</Trans>
                        </label>
                        <Select<SelectOption>
                            {...menuPortalProps}
                            isLoading={sourcesLoading}
                            isDisabled={sourcesLoading || !isNew}
                            isClearable={isNew}
                            placeholder={t`Select a project...`}
                            options={sourceOptions}
                            value={selectedSource}
                            onChange={(option: any) => handleSourceChange(option as SelectOption | null)}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <Trans>Name</Trans>
                            <ValidatingInput
                                className="form-control"
                                value={form.name}
                                onChange={(e) => mergeForm({ name: e.target.value })}
                                placeholder={t`Configurator name`}
                                required
                            />
                        </label>
                    </div>

                    <div className={theme.roomsSection}>
                        <div className={theme.roomsHeader}>
                            <span>
                                <Trans>Assign rooms</Trans>
                            </span>
                            <Checkbox
                                inputSize="md"
                                checked={checkStatus === 'all'}
                                indeterminate={checkStatus === 'some'}
                                onChange={(e) => handleSelectAll(e.target.checked)}>
                                <Trans>Select all</Trans>
                            </Checkbox>
                        </div>
                        <div className={theme.roomList}>
                            {(rooms ?? []).map((room) => {
                                const selected = isSelected(room.id);
                                const roomValue = roomValueOf(room.id);
                                const takenBy = assignedElsewhere.get(room.id);
                                return (
                                    <div key={room.id} className={theme.roomRow}>
                                        <Checkbox
                                            inputSize="md"
                                            checked={selected}
                                            disabled={!!takenBy}
                                            onChange={(e) => toggleRoom(room.id, e.target.checked)}>
                                            {room.icon && <FontAwesomeIcon icon={['fal', room.icon]} fixedWidth />} {room.name}
                                        </Checkbox>
                                        {takenBy && (
                                            <span className={theme.assignedHint}>
                                                <Trans>Assigned to {takenBy}</Trans>
                                            </span>
                                        )}
                                        {selected && (
                                            <div className={theme.roomGlencoe}>
                                                <Select<SelectOption>
                                                    {...menuPortalProps}
                                                    isDisabled={!projectKey || previewLoading}
                                                    isLoading={previewLoading}
                                                    placeholder={t`Select configurator room...`}
                                                    options={glencoeOptions}
                                                    value={glencoeValue(roomValue)}
                                                    onChange={(opt: any) => setRoomValue(room.id, (opt as SelectOption | null)?.value ?? null)}
                                                />
                                                {!roomValue && (
                                                    <span className={theme.requiredHint}>
                                                        <Trans>Pick a configurator room</Trans>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <ValidationSummary />
                </ModalBody>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onClose} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button type="submit" variant="primary" loading={saving} disabled={!form.url.trim() || hasUnmappedRoom}>
                            {isNew ? <Trans>Create</Trans> : <Trans>Save</Trans>}
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>
        </Modal>
    );
};
